/**
 * Metabolic Health Composite Score (0–100)
 *
 * Metabolic health is a leading indicator for mental health. A 2024 JAMA
 * Network Open study (n=200,000+) found elevated glucose, triglycerides,
 * and low HDL predicted depression up to 20 years before diagnosis.
 *
 * 4 sub-components, each scored 0–25:
 * 1. Body Composition (body fat %, BMI)
 * 2. Insulin Sensitivity (fasting insulin, HbA1c, glucose)
 * 3. Lipid Health (HDL, triglycerides, TG/HDL ratio)
 * 4. Cardiorespiratory Fitness (VO2 max)
 */

// ── Types ────────────────────────────────────────────────────────────────────

export type MetricStatus = 'optimal' | 'good' | 'borderline' | 'concerning';

export interface MetabolicMetric {
  name: string;
  value: number | null;
  unit: string;
  status: MetricStatus;
  optimalRange: { min: number; max: number };
  source: string;
}

export interface MetabolicSubScore {
  name: string;
  score: number;
  maxScore: 25;
  status: MetricStatus;
  metrics: MetabolicMetric[];
  mentalHealthNote: string;
  citationKeys: string[];
}

export interface MetabolicScoreResult {
  totalScore: number;
  interpretation: string;
  subScores: MetabolicSubScore[];
  dataCompleteness: number;
  insight: string;
}

// ── Reference Ranges ─────────────────────────────────────────────────────────

export const METABOLIC_RANGES = {
  bodyFat: {
    male:   { optimal: [8, 19],   normal: [6, 24] },
    female: { optimal: [18, 28],  normal: [14, 31] },
  },
  bmi: { optimal: [18.5, 24.9], normal: [18.5, 29.9] },
  fastingInsulin: { optimal: [2, 8], normal: [2, 25] },
  hba1c: { optimal: [4.0, 5.4], normal: [4.0, 5.6] },
  fastingGlucose: { optimal: [70, 90], normal: [70, 99] },
  hdl: {
    male:   { optimal: [50, 100], normal: [40, 100] },
    female: { optimal: [60, 100], normal: [50, 100] },
  },
  triglycerides: { optimal: [0, 100], normal: [0, 149] },
  tgHdlRatio: { optimal: [0, 1.5], normal: [0, 3.0] },
  vo2max: {
    male:   { excellent: [46, 99], good: [39, 45], fair: [34, 38] },
    female: { excellent: [38, 99], good: [32, 37], fair: [28, 31] },
  },
};

export const MENTAL_HEALTH_NOTES: Record<string, { text: string; citations: string[] }> = {
  bodyComposition: {
    text: 'Healthy body composition reduces inflammatory cytokines (IL-6, TNF-α) that drive neuroinflammation and depressive symptoms. Visceral fat produces pro-inflammatory markers that cross the blood-brain barrier and activate neuroinflammatory pathways.',
    citations: [],
  },
  insulinSensitivity: {
    text: 'Insulin resistance doubles the risk of developing major depressive disorder, even in people with no prior history of depression (Watson et al. 2021, Am J Psychiatry). A 2024 JAMA study of 211,200 people found that elevated glucose and triglycerides predicted depression, anxiety, and stress-related disorders up to 20 years before diagnosis.',
    citations: ['watson_2021_insulin_depression', 'chourpiliadis_2024_metabolic_depression'],
  },
  lipidHealth: {
    text: 'Low HDL and elevated triglycerides reflect an inflammatory state associated with mood disorders. The TG/HDL ratio is a reliable insulin resistance marker — a moderate increase was linked to an 89% increase in new cases of major depressive disorder.',
    citations: ['chourpiliadis_2024_metabolic_depression', 'watson_2021_insulin_depression'],
  },
  cardioFitness: {
    text: 'Cardiorespiratory fitness is the single strongest predictor of all-cause mortality, with a 47–59% risk reduction (Lang et al. 2024, 20.9 million observations). Low CRF is associated with a 47% greater risk of depression, and each standard deviation increase reduces depression risk by 29% (Kandola et al. 2019).',
    citations: ['lang_2024_crf_mortality', 'kandola_2019_crf_depression'],
  },
};

export const IMPROVEMENT_TIPS: Record<string, string[]> = {
  'Body Composition': [
    'Resistance training preserves muscle while reducing body fat',
    'Sleep quality directly affects appetite hormones (ghrelin/leptin)',
    'Body composition changes are slow — track trends over months, not days',
  ],
  'Insulin Sensitivity': [
    '30 min of moderate exercise 5x/week reduces insulin resistance by 20-40%',
    'Sleep consistency (your circadian Z-score) directly affects insulin sensitivity',
    'Reducing refined carbohydrates has the most direct impact on fasting insulin',
  ],
  'Lipid Health': [
    'Omega-3 fatty acids (EPA/DHA) reduce triglycerides 15-30%',
    'HDL responds most to regular exercise and dietary healthy fats',
    'TG/HDL ratio < 1.5 is associated with optimal insulin sensitivity',
  ],
  'Cardiorespiratory Fitness': [
    'Zone 2 cardio (conversational pace) 150 min/week is the evidence-based target',
    'VO2 max improves ~5-15% over 8 weeks with consistent training',
    'Higher VO2 max is the strongest single predictor of longevity',
  ],
};

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Score a value against an optimal/normal range. Returns 0-25. */
function scoreMetric(value: number | null, optimal: [number, number], normal: [number, number]): { score: number; status: MetricStatus } {
  if (value == null) return { score: 0, status: 'concerning' };
  if (value >= optimal[0] && value <= optimal[1]) return { score: 25, status: 'optimal' };
  if (value >= normal[0] && value <= normal[1]) return { score: 15, status: 'good' };
  // How far outside normal?
  const distFromNormal = value < normal[0] ? normal[0] - value : value - normal[1];
  const range = normal[1] - normal[0] || 1;
  const severity = Math.min(1, distFromNormal / (range * 0.5));
  if (severity < 0.5) return { score: 8, status: 'borderline' };
  return { score: 3, status: 'concerning' };
}

/** Average non-null scores, returning 0 if none. */
function avgScores(scores: number[]): number {
  const valid = scores.filter(s => s > 0);
  if (valid.length === 0) return 0;
  return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
}

function worstStatus(statuses: MetricStatus[]): MetricStatus {
  if (statuses.includes('concerning')) return 'concerning';
  if (statuses.includes('borderline')) return 'borderline';
  if (statuses.includes('good')) return 'good';
  return 'optimal';
}

// ── Main Scoring Function ────────────────────────────────────────────────────

/** Calculate the Metabolic Health composite score. */
export function calcMetabolicScore(params: {
  bodyFatPercent?: number | null;
  bmi?: number | null;
  fastingInsulin?: number | null;
  hba1c?: number | null;
  fastingGlucose?: number | null;
  hdl?: number | null;
  triglycerides?: number | null;
  vo2max?: number | null;
  sex?: 'male' | 'female';
  age?: number;
}): MetabolicScoreResult {
  const sex = params.sex || 'male';
  const subScores: MetabolicSubScore[] = [];
  let totalAvailable = 0;
  let totalPossible = 4;

  // 1. Body Composition
  const bfRange = sex === 'female' ? METABOLIC_RANGES.bodyFat.female : METABOLIC_RANGES.bodyFat.male;
  const bfResult = scoreMetric(params.bodyFatPercent ?? null, bfRange.optimal as [number, number], bfRange.normal as [number, number]);
  const bmiResult = scoreMetric(params.bmi ?? null, METABOLIC_RANGES.bmi.optimal as [number, number], METABOLIC_RANGES.bmi.normal as [number, number]);
  const bodyMetrics: MetabolicMetric[] = [];
  if (params.bodyFatPercent != null) bodyMetrics.push({ name: 'Body Fat', value: params.bodyFatPercent, unit: '%', status: bfResult.status, optimalRange: { min: bfRange.optimal[0], max: bfRange.optimal[1] }, source: 'apple_health' });
  if (params.bmi != null) bodyMetrics.push({ name: 'BMI', value: params.bmi, unit: 'kg/m²', status: bmiResult.status, optimalRange: { min: 18.5, max: 24.9 }, source: 'apple_health' });
  const bodyScore = avgScores([bfResult.score, bmiResult.score].filter(s => s > 0));
  if (bodyMetrics.length > 0) {
    totalAvailable++;
    subScores.push({ name: 'Body Composition', score: Math.min(25, bodyScore), maxScore: 25, status: worstStatus(bodyMetrics.map(m => m.status)), metrics: bodyMetrics, mentalHealthNote: MENTAL_HEALTH_NOTES.bodyComposition.text, citationKeys: MENTAL_HEALTH_NOTES.bodyComposition.citations });
  }

  // 2. Insulin Sensitivity
  const insulinResult = scoreMetric(params.fastingInsulin ?? null, METABOLIC_RANGES.fastingInsulin.optimal as [number, number], METABOLIC_RANGES.fastingInsulin.normal as [number, number]);
  const hba1cResult = scoreMetric(params.hba1c ?? null, METABOLIC_RANGES.hba1c.optimal as [number, number], METABOLIC_RANGES.hba1c.normal as [number, number]);
  const glucoseResult = scoreMetric(params.fastingGlucose ?? null, METABOLIC_RANGES.fastingGlucose.optimal as [number, number], METABOLIC_RANGES.fastingGlucose.normal as [number, number]);
  const insulinMetrics: MetabolicMetric[] = [];
  if (params.fastingInsulin != null) insulinMetrics.push({ name: 'Fasting Insulin', value: params.fastingInsulin, unit: 'uIU/mL', status: insulinResult.status, optimalRange: { min: 2, max: 8 }, source: 'labs' });
  if (params.hba1c != null) insulinMetrics.push({ name: 'HbA1c', value: params.hba1c, unit: '%', status: hba1cResult.status, optimalRange: { min: 4.0, max: 5.4 }, source: 'labs' });
  if (params.fastingGlucose != null) insulinMetrics.push({ name: 'Glucose', value: params.fastingGlucose, unit: 'mg/dL', status: glucoseResult.status, optimalRange: { min: 70, max: 90 }, source: 'labs' });
  const insulinScore = avgScores([insulinResult.score, hba1cResult.score, glucoseResult.score].filter(s => s > 0));
  if (insulinMetrics.length > 0) {
    totalAvailable++;
    subScores.push({ name: 'Insulin Sensitivity', score: Math.min(25, insulinScore), maxScore: 25, status: worstStatus(insulinMetrics.map(m => m.status)), metrics: insulinMetrics, mentalHealthNote: MENTAL_HEALTH_NOTES.insulinSensitivity.text, citationKeys: MENTAL_HEALTH_NOTES.insulinSensitivity.citations });
  }

  // 3. Lipid Health
  const hdlRange = sex === 'female' ? METABOLIC_RANGES.hdl.female : METABOLIC_RANGES.hdl.male;
  const hdlResult = scoreMetric(params.hdl ?? null, hdlRange.optimal as [number, number], hdlRange.normal as [number, number]);
  const tgResult = scoreMetric(params.triglycerides ?? null, METABOLIC_RANGES.triglycerides.optimal as [number, number], METABOLIC_RANGES.triglycerides.normal as [number, number]);
  const lipidMetrics: MetabolicMetric[] = [];
  if (params.hdl != null) lipidMetrics.push({ name: 'HDL', value: params.hdl, unit: 'mg/dL', status: hdlResult.status, optimalRange: { min: hdlRange.optimal[0], max: hdlRange.optimal[1] }, source: 'labs' });
  if (params.triglycerides != null) lipidMetrics.push({ name: 'Triglycerides', value: params.triglycerides, unit: 'mg/dL', status: tgResult.status, optimalRange: { min: 0, max: 100 }, source: 'labs' });
  if (params.hdl != null && params.triglycerides != null && params.hdl > 0) {
    const ratio = Math.round((params.triglycerides / params.hdl) * 10) / 10;
    const ratioResult = scoreMetric(ratio, METABOLIC_RANGES.tgHdlRatio.optimal as [number, number], METABOLIC_RANGES.tgHdlRatio.normal as [number, number]);
    lipidMetrics.push({ name: 'TG/HDL Ratio', value: ratio, unit: 'ratio', status: ratioResult.status, optimalRange: { min: 0, max: 1.5 }, source: 'labs' });
  }
  const lipidScore = avgScores([hdlResult.score, tgResult.score].filter(s => s > 0));
  if (lipidMetrics.length > 0) {
    totalAvailable++;
    subScores.push({ name: 'Lipid Health', score: Math.min(25, lipidScore), maxScore: 25, status: worstStatus(lipidMetrics.map(m => m.status)), metrics: lipidMetrics, mentalHealthNote: MENTAL_HEALTH_NOTES.lipidHealth.text, citationKeys: MENTAL_HEALTH_NOTES.lipidHealth.citations });
  }

  // 4. Cardiorespiratory Fitness
  const vo2Ranges = sex === 'female' ? METABOLIC_RANGES.vo2max.female : METABOLIC_RANGES.vo2max.male;
  let vo2Score = 0;
  let vo2Status: MetricStatus = 'concerning';
  if (params.vo2max != null) {
    if (params.vo2max >= vo2Ranges.excellent[0]) { vo2Score = 25; vo2Status = 'optimal'; }
    else if (params.vo2max >= vo2Ranges.good[0]) { vo2Score = 20; vo2Status = 'good'; }
    else if (params.vo2max >= vo2Ranges.fair[0]) { vo2Score = 12; vo2Status = 'borderline'; }
    else { vo2Score = 5; vo2Status = 'concerning'; }
    totalAvailable++;
    subScores.push({
      name: 'Cardiorespiratory Fitness', score: vo2Score, maxScore: 25, status: vo2Status,
      metrics: [{ name: 'VO2 Max', value: params.vo2max, unit: 'mL/kg/min', status: vo2Status, optimalRange: { min: vo2Ranges.excellent[0], max: 99 }, source: 'apple_watch' }],
      mentalHealthNote: MENTAL_HEALTH_NOTES.cardioFitness.text, citationKeys: MENTAL_HEALTH_NOTES.cardioFitness.citations,
    });
  }

  // Total score — re-weight if missing sub-components
  const rawTotal = subScores.reduce((s, sub) => s + sub.score, 0);
  const totalScore = totalAvailable > 0 ? Math.round((rawTotal / (totalAvailable * 25)) * 100) : 0;
  const dataCompleteness = totalAvailable / totalPossible;

  const interpretation = totalScore >= 80 ? 'Excellent' : totalScore >= 60 ? 'Good' : totalScore >= 40 ? 'Fair' : 'Needs Attention';
  const insight = generateMetabolicInsight(subScores);

  return { totalScore, interpretation, subScores, dataCompleteness, insight };
}

/** Generate a one-sentence clinical insight based on sub-scores. */
export function generateMetabolicInsight(subScores: MetabolicSubScore[]): string {
  if (subScores.length === 0) return 'Connect wearables and upload labs to see your metabolic health score.';

  const lowest = subScores.reduce((min, s) => s.score < min.score ? s : min);
  const areaMap: Record<string, string> = {
    'Body Composition': 'body composition',
    'Insulin Sensitivity': 'insulin sensitivity',
    'Lipid Health': 'lipid balance',
    'Cardiorespiratory Fitness': 'cardiorespiratory fitness',
  };

  if (lowest.score >= 20) return 'Your metabolic profile is excellent — a strong foundation for mental health.';
  if (lowest.score >= 12) return `Your metabolic profile supports mood stability — ${areaMap[lowest.name] || lowest.name} is your key area to watch.`;
  return `Metabolic markers suggest elevated inflammation risk. Improving ${areaMap[lowest.name] || lowest.name} may benefit both physical and mental health.`;
}
